import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { FC, MouseEvent, useMemo } from 'react'

import { cn } from '@/app/utils'
import { UploadedFileType } from '@/components/shared/file-uploader'
import { DownloadedFileIcon } from '@/components/shared/file-viewer/ui/DownloadedFileIcon'
import { downloadFileByLink, getFileExtensionByPath } from '@/utils/file'

import type { FileCardType } from './FileCard.types'

type Props = FileCardType & {
  onDelete?: (id: string) => void
  withoutDeleting?: boolean
  withoutDownloading?: boolean
  file?: UploadedFileType
  disabled?: boolean
  handleFileAction?: (
    fileName: string,
    image?: string,
    file?: UploadedFileType
  ) => void
  className?: {
    rootClassName?: string
    fileNameClassName?: string
  }
}

const FileCard: FC<Props> = ({
  id,
  fileName = 'Файл',
  error,
  image,
  file,
  fileSizeWithUnit,
  onDelete,
  withoutDownloading,
  withoutDeleting,
  disabled,
  handleFileAction,
  className
}) => {
  const fileExtension = getFileExtensionByPath(fileName || '')
  const fileNameWithoutExtension = fileName?.split('.').slice(0, -1).join('.')

  const fileDescription = useMemo(() => {
    if (fileExtension || fileSizeWithUnit) {
      return (
        <div className="uppercase text-grey-50">
          {fileExtension} {fileSizeWithUnit && `• ${fileSizeWithUnit}`}
        </div>
      )
    }

    return (
      <div className="text-red-70">
        Ошибка загрузки. Попробуйте загрузить снова
      </div>
    )
  }, [fileExtension, fileSizeWithUnit])

  const handleDownloadFile = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    downloadFileByLink(fileName, image ?? file)
  }

  const handleDeleteFile = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (id && onDelete) {
      onDelete(id)
    }
  }

  const handlePreviewFile = () => {
    handleFileAction?.(fileName, image, file)
  }

  const fileInfo = (
    <>
      <div className="flex h-40 items-center justify-center">
        <DownloadedFileIcon fileName={fileName} error={Boolean(error)} />
      </div>
      <div className="min-w-0 pl-8">
        <div className="relative w-full">
          <div
            className={cn(
              'w-full truncate font-pro font-bold text-black transition-opacity text-14 xxl:w-[490px] xl:w-[320px] sm:w-[200px]',
              className?.fileNameClassName
            )}
          >
            {fileNameWithoutExtension || 'Файл'}
          </div>
          {fileDescription}
        </div>
      </div>
    </>
  )

  return (
    <div
      title={fileName}
      data-testid="file-card-id"
      className={cn(
        'flex w-full items-center justify-between rounded-10 bg-white p-8 text-left',
        className?.rootClassName
      )}
    >
      {handleFileAction ? (
        <button
          type="button"
          className="flex min-w-0 flex-1 cursor-pointer text-left"
          data-testid="file-card-preview-button-id"
          onClick={handlePreviewFile}
          disabled={disabled}
        >
          {fileInfo}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1">{fileInfo}</div>
      )}
      <div className="flex shrink-0 justify-end">
        {!withoutDownloading && (
          <button
            aria-label="Скачать файл"
            data-testid="file-card-download-button-id"
            type="button"
            className={cn(
              'flex h-40 w-60 items-center justify-center',
              !withoutDeleting && 'w-40'
            )}
            onClick={handleDownloadFile}
            disabled={disabled}
          >
            <ArrowDownTrayIcon className="h-24 w-24 text-black" />
          </button>
        )}
        {!withoutDeleting && (
          <button
            aria-label="Удалить файл"
            data-testid="file-card-delete-button-id"
            type="button"
            className="flex h-40 w-40 items-center justify-center"
            onClick={handleDeleteFile}
            disabled={disabled}
          >
            <XMarkIcon className="h-24 w-24 text-black" />
          </button>
        )}
      </div>
    </div>
  )
}

export default FileCard
